import { cloneDeep } from "lodash-es";
import { nanoid } from "nanoid";
import { v4 } from "uuid";

import { HoverService } from "@framework/HoverService";
import { SyncSettingsService } from "@framework/SyncSettingsService";
import type { Template } from "@framework/TemplateRegistry";
import { PublishSubscribeDelegate, type PublishSubscribe } from "@lib/utils/PublishSubscribeDelegate";
import { truncateString } from "@lib/utils/strings";
import { UnsubscribeFunctionsManagerDelegate } from "@lib/utils/UnsubscribeFunctionsManagerDelegate";

import type { AtomStoreMaster } from "../AtomStoreMaster";
import { ModuleInstanceTopic, type ModuleInstance } from "../ModuleInstance";
import { ModuleRegistry } from "../ModuleRegistry";

import type { SerializedDashboardState } from "./Dashboard.schema";
import { DASHBOARD_ID_LENGTH, DEFAULT_DASHBOARD_NAME, MAX_DASHBOARD_NAME_LENGTH } from "./persistence/constants";

export type LayoutElement = {
    moduleInstanceId: string;
    moduleName: string;
    relX: number;
    relY: number;
    relHeight: number;
    relWidth: number;
    minimized?: boolean;
    maximized?: boolean;
};

export enum DashboardTopic {
    METADATA = "Metadata",
    LAYOUT = "Layout",
    MODULE_INSTANCES = "ModuleInstances",
    ACTIVE_MODULE_INSTANCE_ID = "ActiveModuleInstanceId",
    SERIALIZED_STATE = "SerializedState",
}

export type DashboardMetadata = {
    name: string;
    description?: string;
};

export type DashboardTopicPayloads = {
    [DashboardTopic.METADATA]: DashboardMetadata;
    [DashboardTopic.LAYOUT]: LayoutElement[];
    [DashboardTopic.MODULE_INSTANCES]: ModuleInstance<any, any>[];
    [DashboardTopic.ACTIVE_MODULE_INSTANCE_ID]: string | null;
    [DashboardTopic.SERIALIZED_STATE]: void;
};

export class Dashboard implements PublishSubscribe<DashboardTopicPayloads> {
    private _publishSubscribeDelegate = new PublishSubscribeDelegate<DashboardTopicPayloads>();
    private _unsubscribeFunctionsManagerDelegate = new UnsubscribeFunctionsManagerDelegate();

    private _id: string;
    private _metadata: DashboardMetadata;
    private _layout: LayoutElement[] = [];
    private _moduleInstances: ModuleInstance<any, any>[] = [];
    private _activeModuleInstanceId: string | null = null;
    private _atomStoreMaster: AtomStoreMaster;
    private _cachedState: SerializedDashboardState | null = null;

    // Per-dashboard framework services. Owned here so their state (synced setting values,
    // current hover data) never leaks between dashboards, even while several dashboards are
    // kept mounted at once by the dashboard hot-cache.
    private _syncSettingsService = new SyncSettingsService();
    private _hoverService = new HoverService();

    constructor(atomStoreMaster: AtomStoreMaster, name?: string) {
        this._id = nanoid(DASHBOARD_ID_LENGTH);
        this._metadata = { name: name ?? DEFAULT_DASHBOARD_NAME };

        this._atomStoreMaster = atomStoreMaster;
    }

    getPublishSubscribeDelegate(): PublishSubscribeDelegate<DashboardTopicPayloads> {
        return this._publishSubscribeDelegate;
    }

    makeSnapshotGetter<T extends DashboardTopic>(topic: T): () => DashboardTopicPayloads[T] {
        const snapshotGetter = (): any => {
            if (topic === DashboardTopic.LAYOUT) {
                return this._layout;
            }
            if (topic === DashboardTopic.MODULE_INSTANCES) {
                return this._moduleInstances;
            }
            if (topic === DashboardTopic.ACTIVE_MODULE_INSTANCE_ID) {
                return this._activeModuleInstanceId;
            }
            if (topic === DashboardTopic.SERIALIZED_STATE) {
                return;
            }
            if (topic === DashboardTopic.METADATA) {
                return this._metadata;
            }

            throw new Error(`No snapshot getter for topic ${topic}`);
        };

        return snapshotGetter;
    }

    getId(): string {
        return this._id;
    }

    getSyncSettingsService(): SyncSettingsService {
        return this._syncSettingsService;
    }

    getHoverService(): HoverService {
        return this._hoverService;
    }

    getMetadata(): DashboardMetadata {
        return this._metadata;
    }

    updateMetadata(metadata: Partial<DashboardMetadata>): void {
        this._metadata = { ...this._metadata, ...metadata };
        this._publishSubscribeDelegate.notifySubscribers(DashboardTopic.METADATA);
        this.handleStateChange();
    }

    getLayout(): LayoutElement[] {
        return this._layout;
    }

    /**
     * Layout for a read-only preview - also for an unloaded dashboard, whose `getLayout()` stays empty
     * until `load()`.
     */
    getLayoutForPreview(): LayoutElement[] {
        if (this._layout.length > 0 || !this._cachedState) {
            return this._layout;
        }

        return this._cachedState.moduleInstances.map((serializedInstance) =>
            Dashboard.makeLayoutElement(
                serializedInstance.moduleInstanceState.id,
                serializedInstance.moduleInstanceState.name,
                serializedInstance.layoutState,
            ),
        );
    }

    setLayout(layout: LayoutElement[]): void {
        this._layout = layout;
        this._publishSubscribeDelegate.notifySubscribers(DashboardTopic.LAYOUT);
        this.handleStateChange();
    }

    getModuleInstances(): ModuleInstance<any, any>[] {
        return this._moduleInstances;
    }

    serializeState(): SerializedDashboardState {
        if (this._cachedState) {
            // Destructuring the cached state and overriding the id, name, and description with the current values.
            return {
                ...this._cachedState,
                id: this._id,
                name: this._metadata.name,
                description: this._metadata.description,
            };
        }

        const moduleInstances = this._moduleInstances.map((moduleInstance) => {
            const moduleInstanceState = moduleInstance.serializeState();

            const layoutInfo = this._layout.find((el) => el.moduleInstanceId === moduleInstance.getId());

            if (!layoutInfo) {
                throw new Error(`Layout info for module instance ${moduleInstance.getId()} not found`);
            }

            return {
                moduleInstanceState,
                layoutState: {
                    relX: layoutInfo.relX,
                    relY: layoutInfo.relY,
                    relHeight: layoutInfo.relHeight,
                    relWidth: layoutInfo.relWidth,
                    minimized: layoutInfo.minimized ?? false,
                    maximized: layoutInfo.maximized ?? false,
                },
            };
        });

        return {
            id: this._id,
            name: this._metadata.name,
            description: this._metadata.description,
            activeModuleInstanceId: this._activeModuleInstanceId,
            moduleInstances,
        };
    }

    deserializeState(serializedDashboard: SerializedDashboardState): void {
        this._id = serializedDashboard.id;
        this._metadata = {
            name: serializedDashboard.name,
            description: serializedDashboard.description,
        };

        this.clearLayout();

        // Stopping here since we don't want to initialize module instances for
        // inactive dashboards. The module instances will be initialized when the dashboard is activated.
        this._cachedState = serializedDashboard;
    }

    initializeModuleInstancesFromCachedState(): void {
        if (!this._cachedState) {
            return;
        }

        const serializedDashboard = this._cachedState;

        try {
            for (const serializedInstance of serializedDashboard.moduleInstances) {
                const { id, name } = serializedInstance.moduleInstanceState;
                this.instantiateModuleInstance(name, id);
            }

            // Doing this after all module instances have been registered
            // ensures that the module instances are available for data channel initialization.
            for (const serializedInstance of serializedDashboard.moduleInstances) {
                const { moduleInstanceState, layoutState } = serializedInstance;
                const moduleInstance = this.getModuleInstance(moduleInstanceState.id);
                if (!moduleInstance) {
                    throw new Error(`Module instance with ID ${moduleInstanceState.id} not found`);
                }

                moduleInstance.initiateDeserialization(moduleInstanceState, this);

                this._layout.push(
                    Dashboard.makeLayoutElement(moduleInstanceState.id, moduleInstanceState.name, layoutState),
                );
            }

            let activeModuleInstanceId = serializedDashboard.activeModuleInstanceId;
            if (activeModuleInstanceId !== null && !this.getModuleInstance(activeModuleInstanceId)) {
                // Stale id, e.g. from old or corrupted persisted data - not worth failing the whole dashboard for
                activeModuleInstanceId = this._moduleInstances.at(-1)?.getId() ?? null;
            }
            this.setActiveModuleInstanceId(activeModuleInstanceId);
        } catch (error) {
            // Remove what this attempt created - a retry recreates the same instance ids from the cached
            // state, and would collide with leftovers
            this.clearLayout();
            throw error;
        }

        this._publishSubscribeDelegate.notifySubscribers(DashboardTopic.LAYOUT);
    }

    clearLayout(): void {
        for (const moduleInstance of this._moduleInstances) {
            this.removeModuleInstance(moduleInstance.getId());
        }
        this._moduleInstances = [];
        this._layout = [];
        this._publishSubscribeDelegate.notifySubscribers(DashboardTopic.LAYOUT);
        this.handleStateChange();
    }

    private handleStateChange(): void {
        this._publishSubscribeDelegate.notifySubscribers(DashboardTopic.SERIALIZED_STATE);
    }

    static makeModuleInstanceId(): string {
        return v4();
    }

    private static makeLayoutElement(
        moduleInstanceId: string,
        moduleName: string,
        layout: Omit<LayoutElement, "moduleInstanceId" | "moduleName">,
    ): LayoutElement {
        return {
            moduleInstanceId,
            moduleName,
            relX: layout.relX,
            relY: layout.relY,
            relHeight: layout.relHeight,
            relWidth: layout.relWidth,
            minimized: layout.minimized,
            maximized: layout.maximized,
        };
    }

    /**
     * Creates a module instance with its bookkeeping only (atom store, subscription) - no layout entry,
     * notifications or activation, so callers adding several at once can do those once. For a single
     * instance, use `makeAndAddModuleInstance`.
     */
    private instantiateModuleInstance(moduleName: string, predefinedId?: string): ModuleInstance<any, any> {
        const module = ModuleRegistry.getModule(moduleName);
        if (!module) {
            throw new Error(`Module ${moduleName} not found`);
        }

        const id = predefinedId ?? Dashboard.makeModuleInstanceId();

        const atomStore = this._atomStoreMaster.makeAtomStoreForModuleInstance(id);

        let moduleInstance: ModuleInstance<any, any>;
        try {
            moduleInstance = module.makeInstance(id, atomStore);
        } catch (error) {
            // Not registered in _moduleInstances yet, so clearLayout() wouldn't clean this up
            this._atomStoreMaster.removeAtomStoreForModuleInstance(id);
            throw error;
        }

        this._moduleInstances = [...this._moduleInstances, moduleInstance];

        this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
            moduleInstance.getId(),
            moduleInstance.makeSubscriberFunction(ModuleInstanceTopic.SERIALIZED_STATE)(
                this.handleStateChange.bind(this),
            ),
        );

        return moduleInstance;
    }

    private unregisterAndUnloadModuleInstance(moduleInstanceId: string): void {
        const moduleInstance = this.getModuleInstance(moduleInstanceId);

        if (!moduleInstance) {
            throw new Error(`Module instance with ID ${moduleInstanceId} not found`);
        }

        this._unsubscribeFunctionsManagerDelegate.unsubscribe(moduleInstanceId);

        try {
            moduleInstance.unload();
        } catch (error) {
            // Removed regardless - stopping here would leave it and its atom store registered, and a
            // teardown (clearLayout()) half done, colliding with the instance's re-creation on the next load()
            console.error(`Failed to unload module instance "${moduleInstanceId}":`, error);
        }

        this._moduleInstances = this._moduleInstances.filter((el) => el.getId() !== moduleInstanceId);

        this._atomStoreMaster.removeAtomStoreForModuleInstance(moduleInstanceId);
    }

    /**
     * Creates a module instance and fully adds it to the live dashboard: notifies
     * MODULE_INSTANCES/LAYOUT subscribers and makes it the active instance. Use this to add a
     * single instance (e.g. from the UI or a template); for bulk creation without per-instance
     * side effects, see `instantiateModuleInstance`.
     */
    makeAndAddModuleInstance(moduleName: string): ModuleInstance<any, any> {
        const moduleInstance = this.instantiateModuleInstance(moduleName);

        this._publishSubscribeDelegate.notifySubscribers(DashboardTopic.MODULE_INSTANCES);
        this._publishSubscribeDelegate.notifySubscribers(DashboardTopic.LAYOUT);

        this.setActiveModuleInstanceId(moduleInstance.getId());

        return moduleInstance;
    }

    removeModuleInstance(moduleInstanceId: string): void {
        this.unregisterAndUnloadModuleInstance(moduleInstanceId);

        const newLayout = this._layout.filter((el) => el.moduleInstanceId !== moduleInstanceId);
        this.setLayout(newLayout);

        this._publishSubscribeDelegate.notifySubscribers(DashboardTopic.MODULE_INSTANCES);
        if (this._activeModuleInstanceId === moduleInstanceId) {
            const lastModuleInstanceId = this._moduleInstances.at(-1)?.getId() ?? null;
            this.setActiveModuleInstanceId(lastModuleInstanceId);
        }
    }

    getModuleInstance(id: string): ModuleInstance<any, any> | null {
        return this._moduleInstances.find((moduleInstance) => moduleInstance.getId() === id) ?? null;
    }

    setActiveModuleInstanceId(moduleInstanceId: string | null): void {
        if (moduleInstanceId !== null && !this.getModuleInstance(moduleInstanceId)) {
            throw new Error(`Module instance with ID ${moduleInstanceId} not found`);
        }
        this._activeModuleInstanceId = moduleInstanceId;
        this._publishSubscribeDelegate.notifySubscribers(DashboardTopic.ACTIVE_MODULE_INSTANCE_ID);
        this.handleStateChange();
    }

    getActiveModuleInstanceId(): string | null {
        return this._activeModuleInstanceId;
    }

    /**
     * Creates the module instances from the cached state. Can throw - PrivateWorkbenchSession only
     * switches to the dashboard if this succeeds.
     */
    load(): void {
        this.initializeModuleInstancesFromCachedState();
        this._cachedState = null;
    }

    /**
     * Tears down the module instances, caching their state for a later `load()` (e.g. on hot-cache
     * eviction). For a dashboard removed for good, use `beforeDestroy()`.
     *
     * Only throws if serializing the state fails - before anything is torn down, so no state is lost.
     */
    unload(): void {
        this._cachedState = this.serializeState();
        this.clearLayout();
    }

    /**
     * Tears down the dashboard's module instances without caching state - use when the dashboard
     * is being permanently removed from the session (or the session itself torn down).
     */
    beforeDestroy(): void {
        this.clearLayout();
    }

    static fromTemplate(template: Template, atomStoreMaster: AtomStoreMaster, id?: string): Dashboard {
        const dashboard = new Dashboard(atomStoreMaster);
        // Callers applying a template to an existing dashboard (rather than creating a brand new
        // one) pass that dashboard's id here so it's preserved - dashboard ids are now part of
        // session/snapshot URLs, so generating a fresh one here would invalidate existing deep
        // links to a dashboard whose layout/content is meant to be its only change.
        if (id) {
            dashboard._id = id;
        }
        dashboard._metadata = {
            name: template.name,
            description: template.description,
        };

        const layout: LayoutElement[] = [];
        const moduleInstances: ModuleInstance<any, any>[] = [];
        const moduleInstanceRefMap: Record<string, ModuleInstance<any, any>> = {};

        for (const module of template.moduleInstances) {
            const moduleInstance = dashboard.makeAndAddModuleInstance(module.moduleName);
            layout.push(Dashboard.makeLayoutElement(moduleInstance.getId(), module.moduleName, module.layout));

            if (module.syncedSettings) {
                for (const syncedSetting of module.syncedSettings) {
                    moduleInstance.addSyncedSetting(syncedSetting);
                }
            }

            if (module.instanceRef) {
                moduleInstanceRefMap[module.instanceRef] = moduleInstance;
            }

            if (module.initialState) {
                moduleInstance.initiateTemplateStateApplication(module.initialState);
            }

            moduleInstances.push(moduleInstance);
        }

        for (const [idx, module] of template.moduleInstances.entries()) {
            const moduleInstance = moduleInstances[idx];
            if (!moduleInstance) {
                throw new Error(`Module instance with reference ${module.instanceRef} not found`);
            }

            if (module.dataChannelsToInitialSettingsMapping) {
                for (const [key, dataChannelConfig] of Object.entries(module.dataChannelsToInitialSettingsMapping)) {
                    const listensToModuleInstance = moduleInstanceRefMap[dataChannelConfig.listensToInstanceRef];
                    if (!listensToModuleInstance) {
                        throw new Error(
                            `Module instance with reference ${dataChannelConfig.listensToInstanceRef} not found`,
                        );
                    }

                    const channel = listensToModuleInstance
                        .getChannelManager()
                        .getChannel(dataChannelConfig.channelIdString);

                    if (!channel) {
                        throw new Error(
                            `Channel with ID ${dataChannelConfig.channelIdString} not found in module instance ${moduleInstance.getId()}`,
                        );
                    }

                    const receiver = moduleInstance.getChannelManager().getReceiver(key);
                    if (!receiver) {
                        throw new Error(
                            `Receiver with ID '${key}' not found in module instance '${moduleInstance.getName()} (${moduleInstance.getId()})'`,
                        );
                    }

                    receiver.connectToChannel(channel, "all");
                }
            }
        }

        dashboard.setLayout(layout);

        return dashboard;
    }

    static clone(source: Dashboard, atomStoreMaster: AtomStoreMaster, name?: string): Dashboard {
        const copySuffix = " (Copy)";
        // Truncate the source name before appending the suffix, not after - appending first and
        // truncating the result could cut the suffix itself off, leaving a copy that doesn't read as
        // one, and truncating without accounting for the suffix at all could push the final name past
        // MAX_DASHBOARD_NAME_LENGTH (the same bound EditDashboardMetadataDialog enforces on user edits).
        const adjustedName =
            name ??
            `${truncateString(source.getMetadata().name, MAX_DASHBOARD_NAME_LENGTH - copySuffix.length)}${copySuffix}`;
        const clonedDashboard = new Dashboard(atomStoreMaster, adjustedName);
        const serializedState = cloneDeep(source.serializeState());

        const moduleInstanceIdMap: Record<string, string> = {};

        // The serialized state needs to be adjusted to change module instance IDs and other unique identifiers to avoid conflicts with the original dashboard.
        serializedState.id = clonedDashboard.getId(); // Ensure the cloned dashboard has a unique ID
        serializedState.name = adjustedName;

        for (const serializedInstance of serializedState.moduleInstances) {
            const { id } = serializedInstance.moduleInstanceState;

            const newId = Dashboard.makeModuleInstanceId();

            // Update the module instance ID and possibly update the active module instance ID if it matches the old ID.
            serializedInstance.moduleInstanceState.id = newId;
            moduleInstanceIdMap[id] = newId;
            if (serializedState.activeModuleInstanceId === id) {
                serializedState.activeModuleInstanceId = newId;
            }
        }

        // Update ids in the data channel manager state to reflect the new module instance IDs.
        for (const serializedInstance of serializedState.moduleInstances) {
            const { dataChannelManagerState } = serializedInstance.moduleInstanceState;
            for (const subscription of dataChannelManagerState.subscriptions) {
                const listensToId = subscription.listensToModuleInstanceId;
                if (moduleInstanceIdMap[listensToId]) {
                    subscription.listensToModuleInstanceId = moduleInstanceIdMap[listensToId];
                }
            }
        }

        clonedDashboard.deserializeState(serializedState);
        return clonedDashboard;
    }
}
